import { useEffect, useState, type ReactNode } from "react";

import { lastServerContactAt } from "../../api/client.ts";
import { useKeepSessionAlive, useSession } from "../../api/hooks.ts";
import { Icon } from "../../ui/Icon.tsx";

/**
 * El aviso de que la sesion esta a punto de cerrarse por inactividad
 * (ADR 0005, punto 2).
 *
 * Avisa ANTES, no despues. Un cierre por inactividad que se descubre al pulsar
 * "Guardar" se lleva por delante el formulario que estabas escribiendo, y eso es
 * exactamente lo que este aviso existe para que no pase.
 *
 * Tres decisiones que explican el codigo:
 *
 * 1. **El reloj se cuenta desde la ultima PETICION**, no desde el ultimo
 *    movimiento del raton (`lastServerContactAt`, en `api/client.ts`). Es lo que
 *    mide el servidor: mover el raton en una pestana abierta no manda nada.
 * 2. **Se descuenta un minuto de margen.** El servidor reescribe el sello de la
 *    cookie como mucho una vez por minuto, asi que su reloj puede ir hasta un
 *    minuto por delante del nuestro. Avisar un minuto antes de la cuenta puede
 *    sobrar; avisar un minuto tarde no avisa.
 * 3. **La aplicacion NO renueva la sesion sola.** Solo la renueva el boton, que
 *    es una persona diciendo que sigue ahi. Si un temporizador pidiera la sesion
 *    cada pocos minutos, una pestana olvidada en un equipo compartido quedaria
 *    abierta para siempre y el cierre por inactividad no cerraria nada.
 */

/** Cada cuanto se recalcula lo que queda. Un segundo seria un render por segundo. */
const TICK_MS = 10_000;

/** El margen del punto 2: el sello del servidor puede ir un minuto adelantado. */
const CLOCK_LAG_MS = 60_000;

export function InactivityToast(): ReactNode {
  const session = useSession();
  const keepAlive = useKeepSessionAlive();
  const [now, setNow] = useState(() => Date.now());
  const [dismissedFor, setDismissedFor] = useState<number | null>(null);

  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), TICK_MS);

    return () => window.clearInterval(timer);
  }, []);

  const minutes = session.data?.inactivityMinutes;

  if (minutes === undefined || minutes <= 0) return null;

  const deadline = lastServerContactAt() + minutes * 60_000 - CLOCK_LAG_MS;
  const remaining = deadline - now;

  if (remaining > warningWindow(minutes)) return null;

  /* Se puede cerrar, como el aviso de version. Y vuelve a salir en cuanto el
     plazo es otro —o sea, cuando la sesion se ha renovado y se acerca el
     siguiente cierre—, porque entonces ya no es el mismo aviso. */
  if (dismissedFor === deadline) return null;

  const expired = remaining <= 0;

  return (
    <div className="toast toast-session show" role="alert">
      <Icon name="clock" />
      <span>
        {expired
          ? "La sesion puede haberse cerrado por inactividad. Comprueba antes de seguir escribiendo."
          : `La sesion se cierra por inactividad en ${remainingText(remaining)}.`}
      </span>
      <button
        type="button"
        className="btn sm toast-action"
        disabled={keepAlive.isPending}
        onClick={() => keepAlive.mutate()}
      >
        {keepAlive.isPending ? "Comprobando…" : expired ? "Comprobar" : "Seguir dentro"}
      </button>
      <button
        type="button"
        className="toast-close"
        aria-label="Cerrar el aviso"
        onClick={() => setDismissedFor(deadline)}
      >
        <Icon name="x" />
      </button>
    </div>
  );
}

/**
 * Con cuanto tiempo por delante se avisa. Tres minutos cuando el plazo es
 * holgado; con plazos cortos —el minimo del servidor son cinco minutos— avisar
 * con tres seria tener el aviso puesto mas tiempo que quitado, y un aviso
 * permanente deja de leerse.
 */
function warningWindow(minutes: number): number {
  return Math.max(1, Math.min(3, Math.floor(minutes / 3))) * 60_000;
}

function remainingText(milliseconds: number): string {
  const minutes = Math.floor(milliseconds / 60_000);

  if (minutes < 1) return "menos de un minuto";

  return `${minutes} ${minutes === 1 ? "minuto" : "minutos"}`;
}
